import * as z from 'zod';

export const CreateShippingOptionRequest = /*#__PURE__*/ z.object({
  shippingOption: z
    .object({
      _id: z
        .string()
        .describe('ShippingOption ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the ShippingOption is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the ShippingOption.\n\nIgnored when creating a ShippingOption.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the ShippingOption was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the ShippingOption was last updated.')
        .optional()
        .nullable(),
      deliveryRegionId: z
        .string()
        .describe('Associated delivery region id.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      deliveryRegionIds: z.array(z.string()).max(50).optional(),
      title: z
        .string()
        .describe('Delivery option configured for the ShippingOption')
        .max(1000)
        .optional()
        .nullable(),
      estimatedDeliveryTime: z
        .string()
        .describe('Estimated delivery time. For example, "2-3 business days".')
        .max(500)
        .optional()
        .nullable(),
      rates: z
        .array(
          z.object({
            amount: z
              .string()
              .describe(
                'there is an AND logic between all the conditions. Empty conditions means true.\nThe amount of the rate that will be returned if all conditions are met.'
              )
              .optional(),
            conditions: z
              .array(
                z.object({
                  type: z
                    .enum([
                      'BY_TOTAL_WEIGHT',
                      'BY_TOTAL_PRICE',
                      'BY_TOTAL_QUANTITY',
                    ])
                    .optional(),
                  value: z
                    .string()
                    .describe(
                      'The value in respective to the condition type\nWeight values should be in the same weight units of the store: KG / LB\nTotal price is according to the store currency\nQuantity of items should be integers'
                    )
                    .optional(),
                  operator: z.enum(['EQ', 'GT', 'GTE', 'LT', 'LTE']).optional(),
                })
              )
              .min(0)
              .max(10)
              .optional(),
            multiplyByQuantity: z
              .boolean()
              .describe(
                'When this flag is set to true, multiply the amount by the number of line items passed on the request.'
              )
              .optional(),
          })
        )
        .min(1)
        .max(50)
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Data Extensions')
        .optional(),
    })
    .describe('ShippingOption to be created.'),
});
export const CreateShippingOptionResponse = /*#__PURE__*/ z.object({
  _id: z
    .string()
    .describe('ShippingOption ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the ShippingOption is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the ShippingOption.\n\nIgnored when creating a ShippingOption.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the ShippingOption was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the ShippingOption was last updated.')
    .optional()
    .nullable(),
  deliveryRegionId: z
    .string()
    .describe('Associated delivery region id.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  deliveryRegionIds: z.array(z.string()).max(50).optional(),
  title: z
    .string()
    .describe('Delivery option configured for the ShippingOption')
    .max(1000)
    .optional()
    .nullable(),
  estimatedDeliveryTime: z
    .string()
    .describe('Estimated delivery time. For example, "2-3 business days".')
    .max(500)
    .optional()
    .nullable(),
  rates: z
    .array(
      z.object({
        amount: z
          .string()
          .describe(
            'there is an AND logic between all the conditions. Empty conditions means true.\nThe amount of the rate that will be returned if all conditions are met.'
          )
          .optional(),
        conditions: z
          .array(
            z.object({
              type: z
                .enum([
                  'BY_TOTAL_WEIGHT',
                  'BY_TOTAL_PRICE',
                  'BY_TOTAL_QUANTITY',
                ])
                .optional(),
              value: z
                .string()
                .describe(
                  'The value in respective to the condition type\nWeight values should be in the same weight units of the store: KG / LB\nTotal price is according to the store currency\nQuantity of items should be integers'
                )
                .optional(),
              operator: z
                .enum(['EQ', 'GT', 'GTE', 'LT', 'LTE'])
                .describe('Logical operator.')
                .optional(),
            })
          )
          .min(0)
          .max(10)
          .optional(),
        multiplyByQuantity: z
          .boolean()
          .describe(
            'When this flag is set to true, multiply the amount by the number of line items passed on the request.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(50)
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Data Extensions')
    .optional(),
});
export const GetShippingOptionRequest = /*#__PURE__*/ z.object({
  shippingOptionId: z
    .string()
    .describe('ID of the ShippingOption to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetShippingOptionResponse = /*#__PURE__*/ z.object({
  _id: z
    .string()
    .describe('ShippingOption ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the ShippingOption is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the ShippingOption.\n\nIgnored when creating a ShippingOption.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the ShippingOption was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the ShippingOption was last updated.')
    .optional()
    .nullable(),
  deliveryRegionId: z
    .string()
    .describe('Associated delivery region id.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  deliveryRegionIds: z.array(z.string()).max(50).optional(),
  title: z
    .string()
    .describe('Delivery option configured for the ShippingOption')
    .max(1000)
    .optional()
    .nullable(),
  estimatedDeliveryTime: z
    .string()
    .describe('Estimated delivery time. For example, "2-3 business days".')
    .max(500)
    .optional()
    .nullable(),
  rates: z
    .array(
      z.object({
        amount: z
          .string()
          .describe(
            'there is an AND logic between all the conditions. Empty conditions means true.\nThe amount of the rate that will be returned if all conditions are met.'
          )
          .optional(),
        conditions: z
          .array(
            z.object({
              type: z
                .enum([
                  'BY_TOTAL_WEIGHT',
                  'BY_TOTAL_PRICE',
                  'BY_TOTAL_QUANTITY',
                ])
                .optional(),
              value: z
                .string()
                .describe(
                  'The value in respective to the condition type\nWeight values should be in the same weight units of the store: KG / LB\nTotal price is according to the store currency\nQuantity of items should be integers'
                )
                .optional(),
              operator: z
                .enum(['EQ', 'GT', 'GTE', 'LT', 'LTE'])
                .describe('Logical operator.')
                .optional(),
            })
          )
          .min(0)
          .max(10)
          .optional(),
        multiplyByQuantity: z
          .boolean()
          .describe(
            'When this flag is set to true, multiply the amount by the number of line items passed on the request.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(50)
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Data Extensions')
    .optional(),
});
export const UpdateShippingOptionRequest = /*#__PURE__*/ z.object({
  _id: z
    .string()
    .describe('ShippingOption ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  shippingOption: z
    .object({
      _id: z
        .string()
        .describe('ShippingOption ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the ShippingOption is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the ShippingOption.\n\nIgnored when creating a ShippingOption.'
        ),
      _createdDate: z
        .date()
        .describe('Date and time the ShippingOption was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the ShippingOption was last updated.')
        .optional()
        .nullable(),
      deliveryRegionId: z
        .string()
        .describe('Associated delivery region id.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      deliveryRegionIds: z.array(z.string()).max(50).optional(),
      title: z
        .string()
        .describe('Delivery option configured for the ShippingOption')
        .max(1000)
        .optional()
        .nullable(),
      estimatedDeliveryTime: z
        .string()
        .describe('Estimated delivery time. For example, "2-3 business days".')
        .max(500)
        .optional()
        .nullable(),
      rates: z
        .array(
          z.object({
            amount: z
              .string()
              .describe(
                'there is an AND logic between all the conditions. Empty conditions means true.\nThe amount of the rate that will be returned if all conditions are met.'
              )
              .optional(),
            conditions: z
              .array(
                z.object({
                  type: z
                    .enum([
                      'BY_TOTAL_WEIGHT',
                      'BY_TOTAL_PRICE',
                      'BY_TOTAL_QUANTITY',
                    ])
                    .optional(),
                  value: z
                    .string()
                    .describe(
                      'The value in respective to the condition type\nWeight values should be in the same weight units of the store: KG / LB\nTotal price is according to the store currency\nQuantity of items should be integers'
                    )
                    .optional(),
                  operator: z.enum(['EQ', 'GT', 'GTE', 'LT', 'LTE']).optional(),
                })
              )
              .min(0)
              .max(10)
              .optional(),
            multiplyByQuantity: z
              .boolean()
              .describe(
                'When this flag is set to true, multiply the amount by the number of line items passed on the request.'
              )
              .optional(),
          })
        )
        .min(1)
        .max(50)
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Data Extensions')
        .optional(),
    })
    .describe('ShippingOption to be updated, may be partial.'),
});
export const UpdateShippingOptionResponse = /*#__PURE__*/ z.object({
  _id: z
    .string()
    .describe('ShippingOption ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the ShippingOption is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the ShippingOption.\n\nIgnored when creating a ShippingOption.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the ShippingOption was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the ShippingOption was last updated.')
    .optional()
    .nullable(),
  deliveryRegionId: z
    .string()
    .describe('Associated delivery region id.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  deliveryRegionIds: z.array(z.string()).max(50).optional(),
  title: z
    .string()
    .describe('Delivery option configured for the ShippingOption')
    .max(1000)
    .optional()
    .nullable(),
  estimatedDeliveryTime: z
    .string()
    .describe('Estimated delivery time. For example, "2-3 business days".')
    .max(500)
    .optional()
    .nullable(),
  rates: z
    .array(
      z.object({
        amount: z
          .string()
          .describe(
            'there is an AND logic between all the conditions. Empty conditions means true.\nThe amount of the rate that will be returned if all conditions are met.'
          )
          .optional(),
        conditions: z
          .array(
            z.object({
              type: z
                .enum([
                  'BY_TOTAL_WEIGHT',
                  'BY_TOTAL_PRICE',
                  'BY_TOTAL_QUANTITY',
                ])
                .optional(),
              value: z
                .string()
                .describe(
                  'The value in respective to the condition type\nWeight values should be in the same weight units of the store: KG / LB\nTotal price is according to the store currency\nQuantity of items should be integers'
                )
                .optional(),
              operator: z
                .enum(['EQ', 'GT', 'GTE', 'LT', 'LTE'])
                .describe('Logical operator.')
                .optional(),
            })
          )
          .min(0)
          .max(10)
          .optional(),
        multiplyByQuantity: z
          .boolean()
          .describe(
            'When this flag is set to true, multiply the amount by the number of line items passed on the request.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(50)
    .optional(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Data Extensions')
    .optional(),
});
export const DeleteShippingOptionRequest = /*#__PURE__*/ z.object({
  shippingOptionId: z
    .string()
    .describe('Id of the ShippingOption to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteShippingOptionResponse = /*#__PURE__*/ z.object({});
export const QueryShippingOptionsRequest = /*#__PURE__*/ z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object in the following format:\n`"filter" : {\n"fieldName1": "value1",\n"fieldName2":{"$operator":"value2"}\n}`\nExample of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(5)
          .optional(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.'
            ),
        }),
      ])
    )
    .describe('WQL expression.'),
});
export const QueryShippingOptionsResponse = /*#__PURE__*/ z.object({
  shippingOptions: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('ShippingOption ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the ShippingOption is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the ShippingOption.\n\nIgnored when creating a ShippingOption.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the ShippingOption was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the ShippingOption was last updated.')
          .optional()
          .nullable(),
        deliveryRegionId: z
          .string()
          .describe('Associated delivery region id.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        deliveryRegionIds: z.array(z.string()).max(50).optional(),
        title: z
          .string()
          .describe('Delivery option configured for the ShippingOption')
          .max(1000)
          .optional()
          .nullable(),
        estimatedDeliveryTime: z
          .string()
          .describe(
            'Estimated delivery time. For example, "2-3 business days".'
          )
          .max(500)
          .optional()
          .nullable(),
        rates: z
          .array(
            z.object({
              amount: z
                .string()
                .describe(
                  'there is an AND logic between all the conditions. Empty conditions means true.\nThe amount of the rate that will be returned if all conditions are met.'
                )
                .optional(),
              conditions: z
                .array(
                  z.object({
                    type: z
                      .enum([
                        'BY_TOTAL_WEIGHT',
                        'BY_TOTAL_PRICE',
                        'BY_TOTAL_QUANTITY',
                      ])
                      .optional(),
                    value: z
                      .string()
                      .describe(
                        'The value in respective to the condition type\nWeight values should be in the same weight units of the store: KG / LB\nTotal price is according to the store currency\nQuantity of items should be integers'
                      )
                      .optional(),
                    operator: z
                      .enum(['EQ', 'GT', 'GTE', 'LT', 'LTE'])
                      .describe('Logical operator.')
                      .optional(),
                  })
                )
                .min(0)
                .max(10)
                .optional(),
              multiplyByQuantity: z
                .boolean()
                .describe(
                  'When this flag is set to true, multiply the amount by the number of line items passed on the request.'
                )
                .optional(),
            })
          )
          .min(1)
          .max(50)
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Data Extensions')
          .optional(),
      })
    )
    .min(0)
    .max(100)
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata')
    .optional(),
});
export const UpdateExtendedFieldsRequest = /*#__PURE__*/ z.object({
  _id: z.string().describe('ID of the entity to update.'),
  namespace: z
    .string()
    .describe(
      'Identifier for the app whose extended fields are being updated.'
    ),
  options: z.object({
    namespaceData: z
      .record(z.string(), z.any())
      .describe(
        'Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured.'
      ),
  }),
});
export const UpdateExtendedFieldsResponse = /*#__PURE__*/ z.object({
  shippingOption: z
    .object({
      _id: z
        .string()
        .describe('ShippingOption ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the ShippingOption is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the ShippingOption.\n\nIgnored when creating a ShippingOption.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the ShippingOption was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the ShippingOption was last updated.')
        .optional()
        .nullable(),
      deliveryRegionId: z
        .string()
        .describe('Associated delivery region id.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      deliveryRegionIds: z.array(z.string()).max(50).optional(),
      title: z
        .string()
        .describe('Delivery option configured for the ShippingOption')
        .max(1000)
        .optional()
        .nullable(),
      estimatedDeliveryTime: z
        .string()
        .describe('Estimated delivery time. For example, "2-3 business days".')
        .max(500)
        .optional()
        .nullable(),
      rates: z
        .array(
          z.object({
            amount: z
              .string()
              .describe(
                'there is an AND logic between all the conditions. Empty conditions means true.\nThe amount of the rate that will be returned if all conditions are met.'
              )
              .optional(),
            conditions: z
              .array(
                z.object({
                  type: z
                    .enum([
                      'BY_TOTAL_WEIGHT',
                      'BY_TOTAL_PRICE',
                      'BY_TOTAL_QUANTITY',
                    ])
                    .optional(),
                  value: z
                    .string()
                    .describe(
                      'The value in respective to the condition type\nWeight values should be in the same weight units of the store: KG / LB\nTotal price is according to the store currency\nQuantity of items should be integers'
                    )
                    .optional(),
                  operator: z
                    .enum(['EQ', 'GT', 'GTE', 'LT', 'LTE'])
                    .describe('Logical operator.')
                    .optional(),
                })
              )
              .min(0)
              .max(10)
              .optional(),
            multiplyByQuantity: z
              .boolean()
              .describe(
                'When this flag is set to true, multiply the amount by the number of line items passed on the request.'
              )
              .optional(),
          })
        )
        .min(1)
        .max(50)
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Data Extensions')
        .optional(),
    })
    .describe('Updated ShippingOption.')
    .optional(),
});
export const AddDeliveryRegionRequest = /*#__PURE__*/ z.object({
  shippingOptionId: z
    .string()
    .describe('ID of the ShippingOption to be updated.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  deliveryRegionId: z
    .string()
    .describe('ID of the delivery region that should use this ShippingOption.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.object({
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe('Revision of the ShippingOption.'),
  }),
});
export const AddDeliveryRegionResponse = /*#__PURE__*/ z.object({
  shippingOption: z
    .object({
      _id: z
        .string()
        .describe('ShippingOption ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the ShippingOption is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the ShippingOption.\n\nIgnored when creating a ShippingOption.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the ShippingOption was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the ShippingOption was last updated.')
        .optional()
        .nullable(),
      deliveryRegionId: z
        .string()
        .describe('Associated delivery region id.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      deliveryRegionIds: z.array(z.string()).max(50).optional(),
      title: z
        .string()
        .describe('Delivery option configured for the ShippingOption')
        .max(1000)
        .optional()
        .nullable(),
      estimatedDeliveryTime: z
        .string()
        .describe('Estimated delivery time. For example, "2-3 business days".')
        .max(500)
        .optional()
        .nullable(),
      rates: z
        .array(
          z.object({
            amount: z
              .string()
              .describe(
                'there is an AND logic between all the conditions. Empty conditions means true.\nThe amount of the rate that will be returned if all conditions are met.'
              )
              .optional(),
            conditions: z
              .array(
                z.object({
                  type: z
                    .enum([
                      'BY_TOTAL_WEIGHT',
                      'BY_TOTAL_PRICE',
                      'BY_TOTAL_QUANTITY',
                    ])
                    .optional(),
                  value: z
                    .string()
                    .describe(
                      'The value in respective to the condition type\nWeight values should be in the same weight units of the store: KG / LB\nTotal price is according to the store currency\nQuantity of items should be integers'
                    )
                    .optional(),
                  operator: z
                    .enum(['EQ', 'GT', 'GTE', 'LT', 'LTE'])
                    .describe('Logical operator.')
                    .optional(),
                })
              )
              .min(0)
              .max(10)
              .optional(),
            multiplyByQuantity: z
              .boolean()
              .describe(
                'When this flag is set to true, multiply the amount by the number of line items passed on the request.'
              )
              .optional(),
          })
        )
        .min(1)
        .max(50)
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Data Extensions')
        .optional(),
    })
    .describe('Updated ShippingOption.')
    .optional(),
});
export const RemoveDeliveryRegionRequest = /*#__PURE__*/ z.object({
  shippingOptionId: z
    .string()
    .describe('ID of the ShippingOption to be updated.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  deliveryRegionId: z
    .string()
    .describe(
      'ID of the delivery region that should not use this ShippingOption anymore.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z.object({
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe('Revision of the ShippingOption.'),
  }),
});
export const RemoveDeliveryRegionResponse = /*#__PURE__*/ z.object({
  shippingOption: z
    .object({
      _id: z
        .string()
        .describe('ShippingOption ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the ShippingOption is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the ShippingOption.\n\nIgnored when creating a ShippingOption.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the ShippingOption was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the ShippingOption was last updated.')
        .optional()
        .nullable(),
      deliveryRegionId: z
        .string()
        .describe('Associated delivery region id.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      deliveryRegionIds: z.array(z.string()).max(50).optional(),
      title: z
        .string()
        .describe('Delivery option configured for the ShippingOption')
        .max(1000)
        .optional()
        .nullable(),
      estimatedDeliveryTime: z
        .string()
        .describe('Estimated delivery time. For example, "2-3 business days".')
        .max(500)
        .optional()
        .nullable(),
      rates: z
        .array(
          z.object({
            amount: z
              .string()
              .describe(
                'there is an AND logic between all the conditions. Empty conditions means true.\nThe amount of the rate that will be returned if all conditions are met.'
              )
              .optional(),
            conditions: z
              .array(
                z.object({
                  type: z
                    .enum([
                      'BY_TOTAL_WEIGHT',
                      'BY_TOTAL_PRICE',
                      'BY_TOTAL_QUANTITY',
                    ])
                    .optional(),
                  value: z
                    .string()
                    .describe(
                      'The value in respective to the condition type\nWeight values should be in the same weight units of the store: KG / LB\nTotal price is according to the store currency\nQuantity of items should be integers'
                    )
                    .optional(),
                  operator: z
                    .enum(['EQ', 'GT', 'GTE', 'LT', 'LTE'])
                    .describe('Logical operator.')
                    .optional(),
                })
              )
              .min(0)
              .max(10)
              .optional(),
            multiplyByQuantity: z
              .boolean()
              .describe(
                'When this flag is set to true, multiply the amount by the number of line items passed on the request.'
              )
              .optional(),
          })
        )
        .min(1)
        .max(50)
        .optional(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Data Extensions')
        .optional(),
    })
    .describe('Updated ShippingOption.')
    .optional(),
});
